import React, { useId, useRef, useState } from "react";
import { Link } from 'react-router-dom';
import styles from "../../styles/campaignBanner.module.css";
import PricingSection from "./PricingSection";

// Import images as JS modules so webpack resolves/bundles them directly —
// this avoids the CSS url() path-resolution issue entirely.
import band1TopNavy from "../../../assets/campaign/band1-top-navy.png";
import band4LightBlue from "../../../assets/campaign/band3-light-blue.png";
import band5LightYellow from "../../../assets/campaign/band5-light-yellow1.jpg";

// Height of the site's sticky/fixed navbar, in pixels. scrollIntoView() lines
// the banner's top edge up with y=0, but the fixed navbar then sits on top
// of that and covers roughly this many pixels — so without subtracting it,
// band 1 ends up hidden behind the navbar and the scroll appears to "stop"
// at band 2 instead. Adjust this if the navbar's height changes.
const STICKY_HEADER_OFFSET = 90;

// Matches the CSS breakpoint already used for mobile-specific layout
// (mobilePlanNavigation / mobilePlans in PricingSection, and the
// `min-width: 768px` band in campaignBanner.module.css). Only below this
// width do we auto-scroll to the pricing table on open — on desktop the
// expanded table is already visible without scrolling, so jumping the
// page would just be disorienting.
const MOBILE_BREAKPOINT_QUERY = "(max-width: 768px)";

// How long to wait after setIsPricingOpen(true) before scrolling, so the
// scroll target has had time to lay out as the section expands. This
// should roughly match PricingSection's own open/expand transition
// duration (in pricingSection.module.css) — adjust if that changes.
const PRICING_EXPAND_SCROLL_DELAY = 350;

// Offset used only for the mobile "open pricing" scroll — kept separate
// from STICKY_HEADER_OFFSET (used for the collapse-scroll-to-banner-top)
// so the two can be tuned independently. Smaller value here = scrolls
// further down = less empty gap between the navbar and the pricing
// section's title bar. Increase this value to leave more gap instead.
const PRICING_SCROLL_OFFSET_MOBILE = 70;

/**
 * CampaignBanner
 * 4-band layout using the real campaign background images:
 *  1. topBand    — band1TopNavy      — price line (gold number) + 料金詳細を見る CTA (top-right)
 *  2. subBand    — flat color #1441E3 — campaign name (no image)
 *  3. lightBand  — band4LightBlue    — trial copy + orange CTA
 *  4. footerBand — flat color        — calendar icon + period
 *
 * Images live in src/jp/assets/campaign/ and are imported directly
 * rather than referenced via CSS url("/images/...") — this project's
 * css-loader resolves url() paths relative to the CSS file's own
 * folder, not the public/ root, so absolute paths there don't work.
 *
 * PricingSection is appended below the 4 bands. Clicking "料金詳細を見る"
 * expands it in place; on phone view this also auto-scrolls down to the
 * pricing table's title bar, since on a small screen the expanded content
 * otherwise appears entirely below the fold and users don't notice it
 * opened. The button inside PricingSection collapses it again and scrolls
 * the view back to the top of the banner.
 */
const CampaignBanner = () => {
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const bannerRef = useRef(null);

  // This page renders CampaignBanner in multiple positions, each with its
  // own PricingSection. A hardcoded id="pricing-detail-section" would be
  // duplicated across instances, and document.getElementById() always
  // returns the FIRST match in the DOM — so every banner's button would
  // scroll to (and label via aria-controls) the first banner's section
  // instead of its own. useId() gives each instance a unique, stable id.
  const pricingSectionId = `pricing-detail-section-${useId()}`;

  const handleTogglePricing = () => {
  const willOpen = !isPricingOpen;
  setIsPricingOpen(willOpen);

  // Auto-scroll to the pricing section whenever it opens — now on all
  // screen sizes (previously mobile-only via matchMedia check).
  if (willOpen) {
    setTimeout(() => {
      const pricingEl = document.getElementById(pricingSectionId);
      if (pricingEl) {
        const pricingTop =
          pricingEl.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: pricingTop - PRICING_SCROLL_OFFSET_MOBILE,
          behavior: "smooth",
        });
      }
    }, PRICING_EXPAND_SCROLL_DELAY);
  }
};

  const handleCollapsePricing = () => {
    setIsPricingOpen(false);
    // The banner sits ABOVE PricingSection in the layout, so collapsing the
    // section (which only shrinks content below the banner) never moves the
    // banner's own position — there's nothing to wait for. Scroll straight
    // away so the two happen together instead of "settle, then jump".
    if (bannerRef.current) {
      // Scroll manually (instead of scrollIntoView) so we can subtract the
      // sticky navbar's height — otherwise the navbar covers band 1 and it
      // looks like the page only scrolled up to band 2.
      const bannerTop =
        bannerRef.current.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: bannerTop - STICKY_HEADER_OFFSET,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className={styles.campaignBanner} ref={bannerRef}>
      {/* Band 1 — dark navy, price + 料金詳細を見る CTA (moved here from band 4) */}
      <div className={styles.topBand} style={{ backgroundImage: `url(${band1TopNavy})` }}>
  <div className={styles.topBandText}>
    <div className={styles.priceLine}>
      運用監視サポート（L1）<span className={styles.priceHighlight}>月額98,000円～</span>
    </div>
    <div className={styles.priceSub}>
      全時間帯同一価格、1シフト/1〜5台のサーバ、オンプレ・クラウド共通
    </div>
  </div>

  <button
    type="button"
    className={`${styles.ctaButton} ${styles.ctaButtonViolet} ${styles.topBandCta}`}
    onClick={handleTogglePricing}
    aria-expanded={isPricingOpen}
    aria-controls={pricingSectionId}
  >
    料金詳細を見る{" "}
    <span className={isPricingOpen ? 'Hide Pricing' : 'View Pricing'}></span>
  </button>
</div>

      {/* Band 2 — campaign name, flat color */}
      <div className={styles.subBand}>
  <span className={styles.subBandInner}>
    DGフューチャーテックインディアのマネージドサービス トライアルキャンペーン
  </span>
</div>

      {/* Band 3 — trial copy + CTA */}
      <div className={styles.lightBand} style={{ backgroundImage: `url(${band4LightBlue})` }}>
        <div className={styles.trialHeadline}>
	  24時間の運用監視をまずは無料で2週間体験 
         {/* まずは<span className={styles.trialHighlight}>2週間</span>、グローバル運用保守の品質を無料で体感 */}

        </div>
        <div className={styles.ctaRow}>
          <span className={styles.trialText}>DG FutureTechの監視・アラート通知品質を、本契約前にご確認いただけます</span>
          <Link to="/jp/campaign-page#trial-scope-section" className={styles.ctaButton}>
            詳細を見る <span className={styles.ctaArrow}>›</span>
          </Link>
        </div>
      </div>

      {/* Band 4 — calendar icon + period */}
      <div className={styles.footerBand} style={{ backgroundImage: `url(${band5LightYellow})` }}>
        <svg
          className={styles.calendarIcon}
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
          <path d="M3 9.5H21" stroke="currentColor" strokeWidth="1.8" />
          <path d="M7.5 3V6.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M16.5 3V6.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="7.2" cy="13" r="0.9" fill="currentColor" />
          <circle cx="12" cy="13" r="0.9" fill="currentColor" />
          <circle cx="16.8" cy="13" r="0.9" fill="currentColor" />
          <circle cx="7.2" cy="17" r="0.9" fill="currentColor" />
          <circle cx="12" cy="17" r="0.9" fill="currentColor" />
          <circle cx="16.8" cy="17" r="0.9" fill="currentColor" />
        </svg>
        キャンペーン期間：26年9月1日～12月31日まで 
        <span className={styles.periodNote}>（申し込み上限に達し次第終了）</span>
      </div>

      {/* Expand/collapse pricing detail */}
      <PricingSection
        sectionId={pricingSectionId}
        isOpen={isPricingOpen}
        onCollapse={handleCollapsePricing}
      />
    </div>
  );
};

export default CampaignBanner;